import React, { Suspense, lazy, useState, useEffect, useRef } from "react";
import { Box, Container, Typography, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import NewAppBar from "components/Navbars/AppBar";
import TextRevealAnimation from "components/textRevealAnimation/textRevealAnimation";
import SkeletonLoaderHeroSection from "./skeletons/SkeletonLoaderHeroSection";

// Lazy load components with priority
const HeroSection = lazy(() => import("./HeroSection"));
const HorizontalJobCards = lazy(() => import("./HorizontalJobCards"));
const HoverEffectCards = lazy(() => import("./FeatureCards"));
const Benefits = lazy(() => import("./Benefits"));
const SubscribeBanner = lazy(() => import("./SubscribeBanner"));
const Footer = lazy(() => import("./Footer"));

// Custom hook for intersection observer with priority loading
const useIntersectionObserver = (threshold = 0.1, rootMargin = "200px") => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasLoaded) {
          setIsVisible(true);
          setHasLoaded(true);
          // Disconnect after first load to prevent re-loading
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [threshold, rootMargin, hasLoaded]);

  return [elementRef, isVisible];
};

// Lazy section wrapper component
const LazySection = ({
  children,
  fallback,
  priority = "normal",
  className,
  sx = {},
  ...props
}) => {
  // Adjust root margin based on priority
  const rootMargin = {
    high: "400px", // Load earlier
    normal: "200px", // Default
    low: "100px", // Load later
  }[priority];

  const [elementRef, isVisible] = useIntersectionObserver(0.1, rootMargin);

  return (
    <Box ref={elementRef} sx={sx} {...props}>
      {isVisible ? (
        <Suspense fallback={fallback}>{children}</Suspense>
      ) : (
        fallback
      )}
    </Box>
  );
};

// Loading components with different heights
const SectionLoader = ({ height = "200px", opacity = 0.5 }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height,
      py: 2,
      opacity,
    }}
  >
    <CircularProgress size={24} />
  </Box>
);

// Skeleton placeholder for better UX
const SkeletonLoader = ({ height = "200px" }) => (
  <Box
    sx={{
      height,
      bgcolor: "grey.100",
      borderRadius: 1,
      position: "relative",
      overflow: "hidden",
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "-100%",
        width: "100%",
        height: "100%",
        background:
          "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
        animation: "shimmer 1.5s infinite",
      },
      "@keyframes shimmer": {
        "0%": { left: "-100%" },
        "100%": { left: "100%" },
      },
    }}
  />
);

export default function LandingPage() {
  const { t } = useTranslation();

  // Preload high-priority components after initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      // Preload critical components
      import("./HeroSection");
      import("./HorizontalJobCards");
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Navigation - Always visible for immediate interaction */}
      <NewAppBar />

      {/* Hero Section - HIGH PRIORITY - Load immediately */}
      <LazySection
        priority="high"
        fallback={<SkeletonLoaderHeroSection height="400px" />}
      >
        <HeroSection />
      </LazySection>

      {/* Jobs Section - HIGH PRIORITY */}
      <LazySection
        priority="normal"
        fallback={<SkeletonLoader height="500px" />}
      >
        <Container sx={{ py: 5 }}>
          <Box
            sx={{
              textAlign: "center",
              mb: 6,
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <TextRevealAnimation
              variant="h3"
              component="h2"
              triggerOnScroll={true} // Enable scroll trigger
              threshold={0.2} // Trigger when 20% of element is visible
              delay={200} // Small delay after element comes into view
              duration={1200}
              sx={{
                fontFamily: "Kontora",
                fontWeight: "bold",
                mb: 1,
                fontSize: { xs: 36, md: 48 },
                display: "inline-flex",
                alignItems: "baseline",
                color: "#000000",
              }}
            >
              {t("landingPage.jobs.title")}{" "}
              <Box
                sx={{
                  color: "#0017E7",
                  fontFamily: "Kontora",
                  display: "inline",
                  ml: 1.5,
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                {t("landingPage.jobs.day")}
              </Box>
            </TextRevealAnimation>
            <TextRevealAnimation
              variant="h3"
              component="h2"
              triggerOnScroll={true} // Enable scroll trigger
              threshold={0.2} // Trigger when 20% of element is visible
              delay={300} // Small delay after element comes into view
              duration={1200}
              color="#00000080"
              sx={{
                fontFamily: "Kontora",
                fontSize: { xs: 18, md: 24 },
                fontWeight: 500,
              }}
            >
              {t("landingPage.jobs.subtitle")}
            </TextRevealAnimation>
          </Box>

          {/* Horizontal Job Cards - HIGH PRIORITY */}
          <LazySection
            priority="high"
            fallback={<SkeletonLoader height="350px" />}
          >
            <HorizontalJobCards />
          </LazySection>
        </Container>
      </LazySection>

      {/* Feature Cards - NORMAL PRIORITY */}
      <LazySection
        priority="normal"
        fallback={<SkeletonLoader height="400px" />}
      >
        <HoverEffectCards />
      </LazySection>

      {/* Benefits Section - NORMAL PRIORITY */}
      <LazySection
        priority="normal"
        fallback={<SkeletonLoader height="500px" />}
      >
        <Benefits />
      </LazySection>

      {/* CTA Section - LOW PRIORITY */}
      <Box sx={{ px: 4 }}>
        <LazySection
          priority="low"
          fallback={<SkeletonLoader height="200px" />}
        >
          <SubscribeBanner />
        </LazySection>
      </Box>

      {/* Footer - LOW PRIORITY */}
      <LazySection priority="low" fallback={<SkeletonLoader height="300px" />}>
        <Footer />
      </LazySection>
    </Box>
  );
}
